// オート周回が止まったあとの「周回の成果」。
// 集めたデータ（AutoHaul。autorun.ts の autoHaulOf が作る）を受け取って見せるだけの部品。周回の仕組みには触れない。
// 上に戦果（戦数・主Lv・戦力・EXP・銭・材木・霊玉）を2行、下に獲得装備を横長の札で縦に並べる（レア度の高い順。能力値・追加効果まで読める）。
// 札は上から順に出る：極は「溜め」（光が集まる）→ 閃光と紙吹雪、優は「バン」と叩きつける、良・並は普通に。
// 画面を押すと早く進む、「スキップ」で全部出す。出たあとは札を押すと詳しい性能。prefers-reduced-motion なら最初から全部。
import { AFFIX_POOL, ENEMIES, EQUIPMENT, EQUIP_SET_BY_ID, affixText, shikiFormName, itemStats, itemTraitEffects, newlyUnlocked, traitText, type Affix, type CharId, type EquipItem, type Rarity } from '@hyakki/core';
import { itemIconStyle, itemOwnerBadge, shikiArtHtml } from '../assets';
import { state } from '../state';
import { sfx, type SfxKey } from '../audio';
import { esc, fmt, resIcon } from './dom';
import { confettiCanvas } from './omikuji-fx';
import { unlockListHtml } from './skill-news';
import { itemInfoDialog } from './equip-view';

/** ドロップした装備1つ（売却されたものは sold） */
export interface HaulDrop {
  defId: string;
  rarity: Rarity;
  affixes: Pick<Affix, 'id' | 'value'>[];
  /** 業物のシリーズ・神品 */
  set?: string;
  shin?: boolean;
  sold?: boolean;
}

/** まとめの入力（周回のあいだに得たもの） */
export interface AutoHaul {
  /** 見出し（無ければ「周回の成果」。掃討は「掃討の成果」） */
  title?: string;
  charId: CharId;
  battles: number;
  wins: number;
  losses: number;
  minutes: number;
  stopReason: string | null;
  /** 主Lv の前後 */
  level: { before: number; after: number };
  /** 周回したキャラの戦力の前後（測れなければ null） */
  power: { before: number; after: number } | null;
  exp: number;
  zeni: number;
  zaimoku: number;
  reidama: number;
  stars: number;
  omamori: number;
  drops: HaulDrop[];
  /** 加入・★が上がった式神 */
  shikigami: { enemyId: string; star: number }[];
}

export type HaulPhase = 'reveal' | 'result';

export interface HaulOpts {
  /** 撮影・確認用：この段で止める（reveal＝最初の極の溜めの途中） */
  holdAt?: HaulPhase;
}

const RAR: Record<Rarity, string> = { n: '並', r: '良', sr: '優', ssr: '極' };
const TIER: Record<Rarity, number> = { n: 1, r: 2, sr: 3, ssr: 4 };
const SLOT: Record<string, string> = { weapon: '武器', armor: '防具', netsuke: 'アクセサリー' };
const STAT: Record<string, string> = { hp: 'HP', atk: '攻撃', def: '防御', agi: '速さ' };
const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
const reduced = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
const SSR_COLORS = ['#ffd86b', '#f7b733', '#e8402a', '#fff4d6', '#c0201c'];
/** 演出で1枚ずつ出す札の数（多いときは残りをまとめて出す） */
const ANIMATE_MAX = 14;

/** ドロップを装備の形に（性能の表示用。強化・突破は0） */
const dropItem = (d: HaulDrop): EquipItem => {
  const def = EQUIPMENT[d.defId];
  return { id: -1, defId: d.defId, slot: def?.slot ?? 'weapon', charId: def?.charId ?? null, tier: def?.tier ?? 1, rarity: d.rarity, level: 0, lb: 0, affixes: d.affixes.map((a) => ({ q: 0.5, ...a })), rerolls: 0, rerollDay: '', ...(d.set ? { set: d.set } : {}), ...(d.shin ? { shin: true } : {}) };
};

/** 一覧の札：極・優・良は1つずつ、並は同じ装備をまとめて数で */
interface Card {
  drop: HaulDrop;
  count: number;
  sold: number;
}
function cardsOf(drops: HaulDrop[]): Card[] {
  const out: Card[] = [];
  const n = new Map<string, Card>();
  for (const d of drops) {
    if (d.rarity !== 'n') {
      out.push({ drop: d, count: 1, sold: d.sold ? 1 : 0 });
      continue;
    }
    const c = n.get(d.defId) ?? { drop: d, count: 0, sold: 0 };
    c.count++;
    if (d.sold) c.sold++;
    n.set(d.defId, c);
  }
  const all = [...out, ...n.values()];
  return all.sort((a, b) => TIER[b.drop.rarity] - TIER[a.drop.rarity] || b.drop.affixes.length - a.drop.affixes.length || b.count - a.count);
}

function cardHtml(c: Card, i: number) {
  const d = c.drop;
  const it = dropItem(d);
  const def = EQUIPMENT[d.defId];
  const set = d.set ? EQUIP_SET_BY_ID[d.set] : undefined;
  const stats = Object.entries(itemStats(it))
    .filter(([, v]) => v)
    .sort((a, b) => b[1] - a[1]);
  const [main, ...rest] = stats;
  const trait = itemTraitEffects(it).map((e) => traitText(e)).join('・');
  const tags = [d.shin ? '<em class="hc-tag shin">神品</em>' : '', set ? `<em class="hc-tag set">業物</em>` : '', def?.meibutsu ? '<em class="hc-tag mb">名物</em>' : ''].join('');
  const allSold = c.count && c.sold >= c.count;
  return `<li class="hc-card r-${d.rarity}${allSold ? ' sold' : ''}" data-i="${i}" tabindex="0" role="button" aria-label="${esc(def?.name ?? d.defId)}の性能">
    <span class="hc-art"><span class="hc-pic" style="${itemIconStyle(d.defId)}"></span>${itemOwnerBadge(d.defId)}<b class="hc-rar">${RAR[d.rarity]}</b>${c.count > 1 ? `<i class="hc-n">×${c.count}</i>` : ''}</span>
    <span class="hc-body">
      <span class="hc-name">${tags}<b>${esc(`${set ? `${set.name}・` : ''}${def?.name ?? d.defId}`)}</b></span>
      <span class="hc-slot">${SLOT[it.slot] ?? ''}・段${it.tier}${allSold ? '<em class="hc-sold">売却</em>' : c.sold ? `<em class="hc-sold">${c.sold}個売却</em>` : ''}</span>
      ${main ? `<span class="hc-main"><small>${STAT[main[0]] ?? main[0]}</small><b>+${fmt(Math.round(main[1] * 10) / 10)}</b>${rest.map(([k, v]) => `<span>${STAT[k] ?? k}+${fmt(Math.round(v * 10) / 10)}</span>`).join('')}</span>` : ''}
      ${trait ? `<span class="hc-trait">${esc(trait)}</span>` : ''}
      ${d.affixes.length ? `<ul class="hc-af">${d.affixes.map((a) => `<li class="${AFFIX_POOL[a.id as Affix['id']]?.drawback ? 'bad' : ''}">${esc(AFFIX_POOL[a.id as Affix['id']] ? affixText({ q: 0.5, ...a } as Affix) : a.id)}</li>`).join('')}</ul>` : ''}
    </span></li>`;
}

/** star：1＝新しく仲間に、0＝また来た（霊玉） */
const shikiCardHtml = (s: { enemyId: string; star: number }) =>
  `<li class="hc-card shiki"><span class="hc-art">${shikiArtHtml(s.enemyId, 76)}<b class="hc-rar">式</b></span>
    <span class="hc-body"><span class="hc-name"><b>${esc(shikiFormName(s.enemyId, Math.max(1, state.profile?.shikigami?.[s.enemyId]?.star ?? 1)))}</b></span><span class="hc-slot">式神</span><span class="hc-main"><b>加入</b></span></span></li>`;

/** 見せて、閉じたら解決する */
export function playHaul(h: AutoHaul, o: HaulOpts = {}): Promise<void> {
  return new Promise((resolve) => {
    const cards = cardsOf(h.drops);
    const unlocked = h.level.after > h.level.before ? newlyUnlocked(h.level.before, h.level.after, h.charId) : [];
    const lvUps = Math.max(0, h.level.after - h.level.before);
    const pw = h.power;
    const pwUp = pw ? pw.after - pw.before : 0;
    const host = document.getElementById('stage') ?? document.body;
    // 前のものが残っていれば閉じる（重ねない）
    document.querySelectorAll('.hf').forEach((x) => x.remove());
    const el = document.createElement('div');
    el.className = 'hf';
    el.setAttribute('role', 'dialog');
    const title = h.title ?? '周回の成果';
    el.setAttribute('aria-label', title);
    const res = (icon: string, v: number) => (v ? `<span>${icon}<b>+${fmt(v)}</b></span>` : '');
    el.innerHTML = `<div class="hf-bg"></div>
      <div class="hf-main">
        <header class="hf-head">
          <h2 class="hf-title">${esc(title)}</h2>
          <small class="hf-sub">${h.battles}戦 ${h.wins}勝${h.losses}敗${h.minutes ? `・${h.minutes}分` : ''}${h.stars ? `・星+${h.stars}` : ''}</small>
          <button class="btn small hf-skip" data-skip>スキップ</button>
          <p class="hf-line"><span>主Lv <b>${h.level.after}</b>${lvUps ? `<em>+${lvUps}</em>` : ''}</span>${pw ? `<span>戦力 <b>${fmt(pw.after)}</b>${pwUp > 0 ? `<em>+${fmt(pwUp)}</em>` : ''}</span>` : ''}</p>
          <p class="hf-line hf-gain">${res('EXP ', h.exp)}${res(resIcon('zeni'), h.zeni)}${res(resIcon('zaimoku'), h.zaimoku)}${res(resIcon('reidama'), h.reidama)}${h.omamori ? `<span>お守り<b>${h.omamori}</b></span>` : ''}</p>
          ${unlocked.length ? `<div class="hf-unlock"><b>新しいスキル</b><span class="su-list">${unlockListHtml(unlocked, 4)}</span></div>` : ''}
        </header>
        <section class="hf-sheet">
          <h3 class="hf-sheet-t">獲得装備<small>${h.drops.length}個</small></h3>
          ${cards.length || h.shikigami.length ? `<ul class="hf-list">${h.shikigami.map(shikiCardHtml).join('')}${cards.map(cardHtml).join('')}</ul>` : '<p class="hf-none">装備は手に入りませんでした</p>'}
        </section>
        <footer class="hf-foot"><button class="btn primary" data-close>閉じる</button></footer>
      </div>
      <div class="hf-charge" hidden><span class="hf-charge-core"></span></div>
      <canvas class="hf-confetti" aria-hidden="true"></canvas>`;
    host.appendChild(el);
    document.body.classList.add('hf-on');
    const list = el.querySelector<HTMLElement>('.hf-list');
    const charge = el.querySelector<HTMLElement>('.hf-charge')!;
    const conf = confettiCanvas(el.querySelector<HTMLCanvasElement>('.hf-confetti')!);
    const items = list ? [...list.children] as HTMLElement[] : [];

    let skip = reduced();
    let hurry = false;
    let done = false;
    let wake: (() => void) | null = null;
    const play = (k: SfxKey, v = 1) => {
      if (!skip) sfx(k, v);
    };
    /** ms 待つ（押すと急ぐ） */
    const pause = async (ms: number) => {
      if (skip || !el.isConnected) return;
      await Promise.race([wait(hurry ? Math.min(ms, 60) : ms), new Promise<void>((r) => (wake = r))]);
      wake = null;
    };
    const sheet = el.querySelector<HTMLElement>('.hf-sheet')!;
    /** 札が見えるよう帳面を縦にだけ送る（scrollIntoView は横にも送るので、出る前の右にずれた札に合わせて左端が見切れていた） */
    const reveal = (li: HTMLElement, center: boolean, smooth = false) => {
      const lr = li.getBoundingClientRect();
      const sr = sheet.getBoundingClientRect();
      let dy = 0;
      if (center) dy = lr.top - sr.top - (sr.height - lr.height) / 2;
      else if (lr.top < sr.top) dy = lr.top - sr.top;
      else if (lr.bottom > sr.bottom) dy = lr.bottom - sr.bottom;
      sheet.scrollTo({ top: sheet.scrollTop + dy, left: 0, behavior: smooth ? 'smooth' : 'auto' });
    };
    const show = (li: HTMLElement, cls = '') => {
      li.classList.add('in');
      if (cls) li.classList.add(cls);
    };
    const finish = () => {
      if (done) return;
      done = true;
      skip = true;
      charge.hidden = true;
      items.forEach((li) => show(li));
      el.classList.add('done');
    };

    el.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (t.closest('[data-close]')) return close();
      if (t.closest('[data-skip]')) {
        finish();
        wake?.();
        return;
      }
      const card = t.closest<HTMLElement>('.hc-card[data-i]');
      if (done && card) {
        const c = cards[Number(card.dataset.i)];
        if (c) itemInfoDialog(dropItem(c.drop), c.sold >= c.count ? '売却済み（所持品には入っていません）' : c.sold ? `${c.count}個のうち${c.sold}個は売却済み` : '');
        return;
      }
      if (!done) {
        hurry = true;
        wake?.();
      }
    });

    const close = () => {
      document.body.classList.remove('hf-on');
      window.removeEventListener('hashchange', close);
      conf.stop();
      el.classList.add('out');
      setTimeout(() => el.remove(), 250);
      resolve();
    };
    window.addEventListener('hashchange', close);

    /** 極：光が集まる溜め（きゅいーん）→ 閃光と紙吹雪で札が出る */
    const ssrReveal = async (li: HTMLElement) => {
      reveal(li, true, !(skip || hurry));
      const r = li.getBoundingClientRect();
      charge.style.setProperty('--cx', `${r.left + r.width * 0.22}px`);
      charge.style.setProperty('--cy', `${r.top + r.height / 2}px`);
      charge.hidden = false;
      charge.classList.remove('burst');
      void charge.offsetWidth;
      charge.classList.add('on');
      play('ougiCharge', 0.9);
      if (o.holdAt === 'reveal') await new Promise<void>(() => {});
      await pause(1500);
      charge.classList.remove('on');
      charge.classList.add('burst');
      play('omikujiSSR');
      show(li, 'pop-ssr');
      conf.burst(skip ? 0 : 160, SSR_COLORS);
      await pause(900);
      charge.hidden = true;
    };
    /** 優：上から叩きつける（バン） */
    const srReveal = async (li: HTMLElement) => {
      reveal(li, false);
      play('omikujiSR', 0.9);
      show(li, 'pop-sr');
      el.classList.remove('shake');
      void el.offsetWidth;
      if (!skip) el.classList.add('shake');
      await pause(520);
    };

    void (async () => {
      await pause(350);
      for (let i = 0; i < items.length && !skip; i++) {
        const li = items[i];
        if (i >= ANIMATE_MAX) break;
        const c = li.dataset.i != null ? cards[Number(li.dataset.i)] : null;
        const t = c ? TIER[c.drop.rarity] : 2;
        if (t >= 4 && !hurry) await ssrReveal(li);
        else if (t === 3 && !hurry) await srReveal(li);
        else {
          reveal(li, false);
          show(li);
          if (t >= 4) play('omikujiSSR', 0.6);
          else if (t === 3) play('omikujiSR', 0.6);
          else play('omikujiFlip', 0.35);
          await pause(t >= 3 ? 200 : 110);
        }
      }
      finish();
      sheet.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    })();
  });
}

// ---------- 開発用：見本で再生（__haulDemo） ----------
/** 見本の周回（極ありで確認する。ssr の数・並の数・優の数を変えられる） */
export function demoHaul(o: { ssr?: number; sr?: number; r?: number; n?: number; levelUps?: number; shiki?: boolean; ssrFrom?: number } = {}): AutoHaul {
  const ids = Object.keys(EQUIPMENT);
  const pick = (k: number, f: (id: string) => boolean = () => true) => ids.filter(f)[k % ids.filter(f).length];
  const line = (id: string) => !EQUIPMENT[id].meibutsu && !EQUIPMENT[id].enemyId;
  const mk = (rarity: Rarity, k: number): HaulDrop => ({ defId: pick(k * 5 + (rarity === 'ssr' ? 40 : 0), line), rarity, affixes: [] });
  const drops: HaulDrop[] = [];
  for (let i = 0; i < (o.n ?? 34); i++) drops.push({ ...mk('n', i % 4), ...(i % 3 === 0 ? { sold: true } : {}) });
  for (let i = 0; i < (o.r ?? 9); i++) drops.push({ ...mk('r', i % Math.max(5, Math.ceil((o.r ?? 9) / 2))), affixes: [{ id: 'atk', value: 0.04 }] });
  for (let i = 0; i < (o.sr ?? 3); i++) drops.push({ ...mk('sr', i + 2), affixes: [{ id: 'hp', value: 0.07 }, { id: 'boss', value: 0.09 }] });
  const meibutsu = ids.find((id) => EQUIPMENT[id].meibutsu);
  const ssrList: HaulDrop[] = [
    { defId: meibutsu ?? pick(1, line), rarity: 'ssr', affixes: [{ id: 'atk', value: 0.072 }, { id: 'skillPower', value: 0.094 }, { id: 'matIncome', value: 1 }] },
    { defId: pick(23, line), rarity: 'ssr', set: Object.keys(EQUIP_SET_BY_ID)[0], affixes: [{ id: 'sutemi', value: 1.05 }, { id: 'core', value: 0.088 }, { id: 'boss', value: 0.11 }, { id: 'regen', value: 0 }] },
    { defId: pick(37, line), rarity: 'ssr', shin: true, affixes: [{ id: 'hp', value: 0.096 }, { id: 'def', value: 0.091 }, { id: 'guard', value: 0.08 }, { id: 'luck', value: 1 }, { id: 'respawn', value: 1 }] },
  ];
  for (let i = 0; i < (o.ssr ?? 2); i++) drops.splice(5 + i * 9, 0, ssrList[(i + (o.ssrFrom ?? 0)) % ssrList.length]);
  const ups = o.levelUps ?? 3;
  const enemy = Object.keys(ENEMIES)[3];
  return {
    charId: 'akari',
    battles: 24,
    wins: 22,
    losses: 2,
    minutes: 38,
    stopReason: '指定の24戦を終えました',
    level: { before: 18, after: 18 + ups },
    power: { before: 8240, after: 8240 + ups * 310 },
    exp: 18_420,
    zeni: 12_850,
    zaimoku: 960,
    reidama: 0,
    stars: 2,
    omamori: 1,
    drops,
    shikigami: o.shiki === false || !enemy ? [] : [{ enemyId: enemy, star: 1 }],
  };
}
